import * as React from 'react';
import './AboutPage.css';

function AboutPage() {
  return (
    <div className="container" id="about">
      <div className="row"></div>
      <div className="col-md-9 col-md-push-3">
        <h1 className="title">
          <span>Biomedical Data Integration demo</span>
        </h1>
        <p>
          IMPaCT-Data is the IMPaCT program aimed at supporting the development
          of a common, interoperable, and integrated system for the collection
          and analysis of clinical and molecular data, contributing to this goal
          with the knowledge and resources available in the Spanish Science and
          Technology System. This development will allow researchers to have a
          population-wide perspective based on individual data.
        </p>
        <p>
          The main objective of IMPaCT-Data is to create the first iteration, as
          a proof of concept, of an infrastructure for the secondary use of data
          from Spanish healthcare systems, which include electronic health
          records, medical imaging data, and data stored in genomic
          repositories. The ultimate goal is to contribute to the healthcare
          system with the knowledge and methodology generated, enabling
          researchers to address research questions using the available data. By
          providing a population-based perspective, this platform supports the
          advancement of scientific discoveries and the improvement of
          healthcare services. This initiative has national distribution, with a
          total of 47 participating institutions from 15 Autonomous Communities.
        </p>
        <p>
          One of the technical objectives of IMPaCT-Data is the development of
          prototypes for integrating the results of genomic and imaging analysis
          with the standardized information extracted from Electronic Health
          Records (EHRs). These prototypes provide researchers with tools that
          allow them to discover, through integrated queries, whether the data
          repositories contain the necessary data to conduct a particular study.
          Once the relevant data is identified, the researcher must request
          access in accordance with the repository's data access regulations and
          policies, and if access is granted, the data can be downloaded to use
          analysis and visualization tools to carry out the authorized study.
        </p>
        <p>
          One of the main stages in the integration process is the discovery
          stage, as previously mentioned. At this stage, Beacon-type solutions
          can be used. The Beacon protocol is a specification driven by the
          Global Alliance for Genomics and Health (GA4GH) initiative, which
          defines an open standard for the discovery of genomic and phenotypic
          data in biomedical research and clinical applications. However, this
          protocol can be adapted to other types of data, such as clinical data
          or medical imaging data.
        </p>

        <p style={{ textAlign: 'center' }}>
          <img
            src={`${process.env.PUBLIC_URL}/diagrame.png`}
            alt="Italian Trulli"
          ></img>
        </p>

        <p>
          In this sense, different instances of the Beacon protocol have been
          used for different types of data (genomic, clinical, and medical
          imaging) in order to develop a discovery tool through integrated
          queries.
        </p>
        <p>
          More specifically, this integrator makes use of different components
          developed in the reference implementation of IMPaCT-Data (
          <a
            href="https://impact-data-ref-imp.readthedocs.io/es/latest/index.html"
            target="_blank"
            rel="noopener noreferrer"
          >
            https://impact-data-ref-imp.readthedocs.io/es/latest/index.html
          </a>
          ):
          <ul>
            <li>
              Beacon v2, for the discovery of genomic data:{' '}
              <a
                href="https://b2ri-documentation.readthedocs.io/en/latest/"
                target="_blank"
                rel="noopener noreferrer"
              >
                https://b2ri-documentation.readthedocs.io/en/latest/
              </a>
              , adapted to pathogen data, in this case, viral genomes of
              SARS-CoV-2.
            </li>
            <li>
              Beacon-OMOP (B4OMOP), for the discovery of clinical data. This is
              a development derived from the aforementioned Beacon v2, which
              allows the integration of a Beacon into any OMOP CDM database.{' '}
              <a
                href="https://gitlab.bsc.es/impact-data/impd-beacon_omopcdm/-/blob/main/README.md"
                target="_blank"
                rel="noopener noreferrer"
              >
                https://gitlab.bsc.es/impact-data/impd-beacon_omopcdm/-/blob/main/README.md
              </a>
            </li>
            <li>
              Beacon4Images, for the discovery of imaging data:{' '}
              <a
                href="https://github.com/EGA-archive/beacon2-ri-api-images"
                target="_blank"
                rel="noopener noreferrer"
              >
                https://github.com/EGA-archive/beacon2-ri-api-images
              </a>
            </li>
          </ul>
        </p>

        <p>
          For each component, real or synthetic datasets corresponding to 319
          patients are used, as follows:
        </p>
        <p>
          <ul>
            <li>
              For the genomic beacon, genomic data of SARS-CoV-2 from a set of
              319 individuals diagnosed with SARS-CoV-2 at Hospital Virgen del
              Rocío is used. Of these 319 individuals, 310 have viral genomic
              data, which allows querying the absence or presence of specific
              genomic variants.
            </li>
            <li>
              For the clinical beacon, limited clinical data from the total of
              319 patients is used. This includes information such as the
              patient's gender, medical history, or specific measurements from
              various lab tests.
            </li>
            <li>
              For the medical imaging beacon, a synthetic dataset has been built
              for the 319 individuals, providing information about the diagnosis
              made based on the medical image and the anatomical location where
              the image was taken. Thus, a total of 626 diagnostic entries and
              630 diagnostic test entries have been generated.
            </li>
          </ul>
        </p>
      </div>
    </div>
  );
}

export default AboutPage;
